<template>
  <br/>
  <div class="row">
    <div class="col-12 mb-3">
      <h4>Possible fraud payments</h4>
    </div>
    <div class="col-lg-3 col-md-6 col-sm-12 mb-3">
      <label for="possible-fraud-status" class="form-label me-1 mt-2">Status</label>
      <select id="possible-fraud-status" class="form-control" v-model="reviewStatus" @change="filterChanged">
        <option value="">All</option>
        <option value="not_reviewed">Not reviewed</option>
        <option value="reviewed">Reviewed</option>
      </select>
    </div>
    <div class="col-lg-3 col-md-6 col-sm-12 mb-3">
      <label for="possible-fraud-currency" class="form-label me-1 mt-2">Currency</label>
      <select id="possible-fraud-currency" class="form-control" v-model="currency" @change="filterChanged">
        <option value="">All</option>
        <option v-for="option in currencies" :key="option" :value="option">{{ option }}</option>
      </select>
    </div>
    <div class="col-lg-3 col-md-6 col-sm-12 mb-3">
      <label for="possible-fraud-provider" class="form-label me-1 mt-2">Payment provider</label>
      <select id="possible-fraud-provider" class="form-control" v-model="paymentProvider" @change="filterChanged">
        <option value="">All</option>
        <option value="stripe">Stripe</option>
        <option value="ipay">IPay</option>
      </select>
    </div>
    <div class="col-lg-3 col-md-6 col-sm-12 mb-3">
      <label for="possible-fraud-payment-type" class="form-label me-1 mt-2">Payment type</label>
      <select id="possible-fraud-payment-type" class="form-control" v-model="paymentType" @change="filterChanged">
        <option value="">All</option>
        <option v-for="option in paymentTypes" :key="option.value" :value="option.value">{{ option.label }}</option>
      </select>
    </div>
    <div class="col-lg-3 col-md-6 col-sm-12 mb-3">
      <label for="possible-fraud-paid-from" class="form-label me-1 mt-2">Date from</label>
      <input
        id="possible-fraud-paid-from"
        type="date"
        class="form-control"
        v-model="paidFrom"
        :max="paidTo"
        @input="filterChanged"
      />
    </div>
    <div class="col-lg-3 col-md-6 col-sm-12 mb-3">
      <label for="possible-fraud-paid-to" class="form-label me-1 mt-2">Date to</label>
      <input
        id="possible-fraud-paid-to"
        type="date"
        class="form-control"
        v-model="paidTo"
        :min="paidFrom"
        @input="filterChanged"
      />
    </div>
    <div class="col-lg-3 col-md-6 col-sm-12 mb-3">
      <label for="possible-fraud-per-page" class="form-label me-1 mt-2">Per page</label>
      <select id="possible-fraud-per-page" class="form-control" v-model="perPage" @change="filterChanged">
        <option :value="10">10</option>
        <option :value="20">20</option>
        <option :value="50">50</option>
        <option :value="100">100</option>
      </select>
    </div>

    <div class="table-responsive">
      <table class="table possible-fraud-payments-table">
        <thead>
        <tr>
          <th scope="col">Review status</th>
          <th scope="col">Creator name</th>
          <th scope="col" class="possible-fraud-creator-link">Creator link</th>
          <th scope="col">Payment type</th>
          <th scope="col">Amount</th>
          <th scope="col">Currency</th>
          <th scope="col">Paid at</th>
          <th scope="col">Payment provider</th>
          <th scope="col">Reference number</th>
          <th scope="col">Payer name</th>
          <th scope="col">Payer email</th>
          <th scope="col">Payer country</th>
          <th scope="col" class="possible-fraud-action-cell">Action</th>
        </tr>
        </thead>
        <tbody>
        <tr v-if="loading">
          <td colspan="14" class="text-center">
            <font-awesome-icon icon="fa-solid fa-sync fa-10x" spin/>
          </td>
        </tr>
        <tr v-else-if="possibleFraudPayments.length > 0" v-for="payment in possibleFraudPayments" :key="payment.id">
          <td>
            <span :class="reviewStatusBadgeClass(payment)">
              {{ valueOrPlaceholder(payment.review_status_label) }}
            </span>
          </td>
          <td>{{ valueOrPlaceholder(payment.creator_name) }}</td>
          <td class="possible-fraud-creator-link">
            <a v-if="isSafeUrl(payment.creator_link)" :href="payment.creator_link" target="_blank" rel="noopener noreferrer">
              {{ payment.creator_link }}
            </a>
            <span v-else>{{ payment.creator_link || '-' }}</span>
          </td>
          <td>{{ formatPaymentType(payment) }}</td>
          <td>{{ formatAmount(payment) }}</td>
          <td>{{ valueOrPlaceholder(payment.currency) }}</td>
          <td>{{ formatDateTime(payment.paid_at) }}</td>
          <td>{{ formatProvider(payment.payment_provider) }}</td>
          <td>
            <a
              v-if="payment.payment_link"
              :href="payment.payment_link"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ payment.payment_number || 'Click here' }}
            </a>
            <span v-else>{{ payment.payment_number || '-' }}</span>
          </td>
          <td>{{ valueOrPlaceholder(payment.payer_name) }}</td>
          <td>
            <a v-if="isSafeEmail(payment.payer_email)" :href="`mailto:${payment.payer_email}`">{{ payment.payer_email }}</a>
            <span v-else>{{ payment.payer_email || '-' }}</span>
          </td>
          <td>{{ valueOrPlaceholder(payment.payer_country) }}</td>
          <td class="possible-fraud-action-cell">
            <div class="d-flex align-items-center gap-2 flex-nowrap">
              <select
                class="form-control possible-fraud-action-select"
                :value="payment.review_status"
                :disabled="isUpdatingReviewStatus(payment.id)"
                @change="updateReviewStatus(payment, $event)"
              >
                <option value="not_reviewed">Not reviewed</option>
                <option value="reviewed">Reviewed</option>
              </select>
              <span v-if="isUpdatingReviewStatus(payment.id)" class="flex-shrink-0" aria-hidden="true">
                <font-awesome-icon icon="fa-solid fa-sync" spin/>
              </span>
            </div>
          </td>
        </tr>
        <tr v-else>
          <td colspan="14" class="text-center">No possible fraud payments found</td>
        </tr>
        </tbody>
      </table>

      <div class="d-flex justify-content-between" v-if="totalRecords >= 1">
        <div>
          <div class="p-2">
            <p>Total possible fraud payments: {{ totalRecords }}</p>
          </div>
        </div>
        <div>
          <div class="p-2">
            <nav v-if="totalPages >= 1" aria-label="Possible fraud payments pagination">
              <ul class="pagination">
                <li class="page-item" :class="{disabled: currentPage - 1 < 1}">
                  <button
                    type="button"
                    class="page-link"
                    :disabled="currentPage - 1 < 1"
                    aria-label="Previous page"
                    @click="paginate(currentPage - 1)"
                  >
                    Previous
                  </button>
                </li>
                <li class="page-item" :class="{active: 1 === currentPage}">
                  <button
                    type="button"
                    class="page-link"
                    :class="{'not-clickable': currentPage === 1}"
                    :disabled="currentPage === 1"
                    :aria-current="currentPage === 1 ? 'page' : null"
                    @click="paginate(1)"
                  >
                    1
                  </button>
                </li>
                <li class="page-item" v-if="currentPage - 2 > 1">
                  <span class="page-link">...</span>
                </li>
                <li class="page-item" v-if="currentPage - 2 > 1">
                  <button type="button" class="page-link" @click="paginate(currentPage - 2)">{{ currentPage - 2 }}</button>
                </li>
                <li class="page-item" v-if="currentPage - 1 > 1">
                  <button type="button" class="page-link" @click="paginate(currentPage - 1)">{{ currentPage - 1 }}</button>
                </li>
                <li class="page-item" v-if="currentPage !== 1" :class="{active: currentPage}">
                  <button
                    type="button"
                    class="page-link"
                    :class="{'not-clickable': currentPage}"
                    :aria-current="currentPage ? 'page' : null"
                    @click="paginate(currentPage)"
                  >
                    {{ currentPage }}
                  </button>
                </li>
                <li class="page-item" v-if="currentPage + 1 <= totalPages">
                  <button type="button" class="page-link" @click="paginate(currentPage + 1)">{{ currentPage + 1 }}</button>
                </li>
                <li class="page-item" v-if="currentPage + 2 <= totalPages">
                  <button type="button" class="page-link" @click="paginate(currentPage + 2)">{{ currentPage + 2 }}</button>
                </li>
                <li class="page-item" v-if="currentPage + 2 < totalPages">
                  <span class="page-link">...</span>
                </li>
                <li
                  class="page-item"
                  v-if="currentPage !== totalPages && currentPage + 2 < totalPages"
                  :class="{active: totalPages === currentPage}"
                >
                  <button
                    type="button"
                    class="page-link"
                    :class="{'not-clickable': totalPages === currentPage}"
                    :disabled="totalPages === currentPage"
                    :aria-current="totalPages === currentPage ? 'page' : null"
                    @click="paginate(totalPages)"
                  >
                    {{ totalPages }}
                  </button>
                </li>
                <li class="page-item" :class="{disabled: currentPage + 1 > totalPages}">
                  <button
                    type="button"
                    class="page-link"
                    :disabled="currentPage + 1 > totalPages"
                    aria-label="Next page"
                    @click="paginate(currentPage + 1)"
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import _debounce from "lodash/debounce";
import {
  buildPossibleFraudPaymentsListParams,
  formatPossibleFraudPaymentType,
  PAYMENT_TYPE_OPTIONS,
} from './possibleFraudPayments.mjs';

function applyPossibleFraudPaymentsPayload(vm, body, requestedPage) {
  const data = body && typeof body === 'object' ? body : {};
  const meta = data.meta || {};

  vm.possibleFraudPayments = Array.isArray(data.data) ? data.data : [];
  vm.totalRecords = meta.total != null ? meta.total : vm.possibleFraudPayments.length;
  vm.totalPages = meta.last_page != null ? meta.last_page : 1;
  vm.currentPage = meta.current_page != null ? meta.current_page : requestedPage;
}

export default {
  data() {
    return {
      loading: true,
      possibleFraudPayments: [],
      currentPage: 1,
      totalPages: 0,
      totalRecords: 0,
      perPage: 20,
      reviewStatus: '',
      currency: '',
      paymentProvider: '',
      paymentType: '',
      paidFrom: '',
      paidTo: '',
      currencies: ['EUR', 'GBP', 'USD', 'UAH'],
      paymentTypes: PAYMENT_TYPE_OPTIONS,
      updatingReviewStatusIds: [],
      _possibleFraudReqId: 0,
    }
  },

  mounted() {
    this.getPossibleFraudPayments();
  },

  methods: {
    buildListParams(page) {
      return buildPossibleFraudPaymentsListParams({
        page,
        perPage: this.perPage,
        reviewStatus: this.reviewStatus,
        currency: this.currency,
        paymentProvider: this.paymentProvider,
        paymentType: this.paymentType,
        paidFrom: this.paidFrom,
        paidTo: this.paidTo,
      });
    },
    debounceGetPossibleFraudPayments: _debounce(function () {
      this.loading = true;
      const page = this.currentPage;
      const requestId = ++this._possibleFraudReqId;

      axios.get('/admin/possible-fraud-payments/list', { params: this.buildListParams(page) })
        .then(response => {
          if (requestId !== this._possibleFraudReqId) {
            return;
          }

          applyPossibleFraudPaymentsPayload(this, response.data, page);
          this.loading = false;
        })
        .catch(error => {
          if (requestId !== this._possibleFraudReqId) {
            return;
          }

          console.log(error);
          this.loading = false;
          this.$toast.error('Failed to load possible fraud payments.');
        });
    }, 500),
    getPossibleFraudPayments() {
      this.debounceGetPossibleFraudPayments();
    },
    filterChanged() {
      if (this.paidFrom && this.paidTo && this.paidFrom > this.paidTo) {
        this.paidTo = this.paidFrom;
      }

      this.currentPage = 1;
      this.getPossibleFraudPayments();
    },
    paginate(page) {
      if (this.totalPages > 0 && (page < 1 || page > this.totalPages)) return;
      this.currentPage = page;
      this.getPossibleFraudPayments();
    },
    isSafeUrl(url) {
      if (!url || typeof url !== 'string') return false;
      try {
        const { protocol } = new URL(url);
        return protocol === 'http:' || protocol === 'https:';
      } catch {
        return false;
      }
    },
    isSafeEmail(email) {
      if (!email || typeof email !== 'string') return false;
      return /^[^\s@"'<>]+@[^\s@"'<>]+\.[^\s@"'<>]+$/.test(email);
    },
    valueOrPlaceholder(value) {
      return value !== null && value !== undefined && value !== '' ? value : '-';
    },
    formatAmount(payment) {
      const amount = this.valueOrPlaceholder(payment.amount_display);
      const currency = this.valueOrPlaceholder(payment.currency);

      if (amount === '-' && currency === '-') {
        return '-';
      }

      return `${amount} ${currency}`;
    },
    formatProvider(provider) {
      const providerMap = {
        stripe: 'Stripe',
        ipay: 'IPay',
      };

      return providerMap[provider] || this.valueOrPlaceholder(provider);
    },
    formatPaymentType(payment) {
      return formatPossibleFraudPaymentType(payment && payment.payment_type_name, payment && payment.payment_type);
    },
    formatDateTime(value) {
      if (!value) {
        return '-';
      }

      const date = new Date(String(value).replace(' ', 'T'));

      if (Number.isNaN(date.getTime())) {
        return value;
      }

      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },
    reviewStatusBadgeClass(payment) {
      if (payment.review_status_highlight === 'green' || payment.review_status === 'reviewed') {
        return 'badge badge-success';
      }

      return 'badge badge-secondary';
    },
    isUpdatingReviewStatus(id) {
      return this.updatingReviewStatusIds.includes(id);
    },
    setReviewStatusUpdating(id, updating) {
      if (updating) {
        this.updatingReviewStatusIds.push(id);
        return;
      }

      this.updatingReviewStatusIds = this.updatingReviewStatusIds.filter(updatingId => updatingId !== id);
    },
    updateReviewStatus(payment, event) {
      const reviewStatus = event.target.value;
      const previousReviewStatus = payment.review_status;

      if (reviewStatus === previousReviewStatus) {
        return;
      }

      this.setReviewStatusUpdating(payment.id, true);

      axios.patch(`/admin/possible-fraud-payments/${payment.id}/review-status`, {
        review_status: reviewStatus,
      })
        .then(response => {
          const responsePayment = response.data && response.data.data ? response.data.data : response.data;
          const updatedPayment = responsePayment && responsePayment.id ? responsePayment : {
            ...payment,
            review_status: reviewStatus,
            review_status_label: reviewStatus === 'reviewed' ? 'Reviewed' : 'Not reviewed',
            review_status_highlight: reviewStatus === 'reviewed' ? 'green' : null,
          };

          Object.assign(payment, updatedPayment);
          this.$toast.success('Review status updated successfully!');
        })
        .catch(error => {
          console.log(error);
          event.target.value = previousReviewStatus;
          this.$toast.error('Failed to update review status.');
        })
        .finally(() => {
          this.setReviewStatusUpdating(payment.id, false);
        });
    },
  }
}
</script>

<style scoped>

.possible-fraud-payments-table .possible-fraud-creator-link {
  width: 150px;
}

.possible-fraud-payments-table .possible-fraud-creator-link a {
  word-break: break-all;
}

.possible-fraud-payments-table .possible-fraud-action-cell {
  position: sticky;
  right: 0;
  z-index: 2;
  min-width: 12.5rem;
  vertical-align: middle;
  background-color: var(--bs-body-bg, #fff);
  box-shadow: -6px 0 12px -6px rgba(0, 0, 0, 0.15);
}

.possible-fraud-payments-table thead .possible-fraud-action-cell {
  z-index: 3;
}

.possible-fraud-payments-table .possible-fraud-action-select {
  min-width: 10.75rem;
  flex: 0 0 auto;
}
</style>
